import React, { useEffect, useState } from "react";
import styles from "./apply-my-blueprint-library-modal-content.module.scss";
import closeIcon from "@src/assets/svg/close-icon.svg";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import backArrowBlueIcon from "@src/assets/svg/back-arrow-blue.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  updateMyBlueprintLibraryModalContentShown,
  updateSelectedMyBlueprintLibraryBlueprint,
  updateShowMyBlueprintLibraryModal,
} from "@src/reducers/your-reducer";
import { RootState } from "@src/reducers/store";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { Button } from "@src/components/atoms/buttons/button/button";
import { ReplaceBlueprintGradientBox } from "@src/components/organisms/replace-blueprint-gradient-box/replace-blueprint-gradient-box";
import { BlueprintsArrayObjectTypes } from "@common/types/blueprint-types";
import { findSelectedBlueprintUseCase } from "@src/cloud-connect/blueprint/use-cases/find-selected-blueprint-use-case";
import { GlassyModalHeader } from "@src/components/molecules/glassy-modal-header/glassy-modal-header";
import { applyBlueprint } from "@src/cloud-connect/use-cases/apply-blueprint";
import { getTileCollection } from "@src/cloud-connect/dependency-injection-container/get-tile-collection";
import { TileStructureParser } from "@src/cloud-connect/blueprint/dashboards/entities/tile-structure-parser";
import { useI18N } from "@src/language/language-context";

const cssClassName = "apply-my-blueprint-library-modal-content";

export const ApplyMyBlueprintLibraryModalContent = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const selectedFindBlueprint = useSelector(
    (state: RootState) =>
      state.yourSlice.myBlueprintLibraryModal.selectedBlueprint,
  );

  const machineStructureData = useSelector(
    (state: RootState) => state.yourSlice.editStructurePageTransferData,
  );

  const [currentStructure, setCurrentStructure] =
    useState<BlueprintsArrayObjectTypes>();
  const [proposedBlueprintStructure, setProposedBlueprintStructure] = useState<
    BlueprintsArrayObjectTypes | undefined
  >();

  const texts = {
    subTitle: i18n("modules.applyMyBlueprintLibrary.subTitle"),
    title: i18n("modules.applyMyBlueprintLibrary.title"),
    description: i18n("modules.applyMyBlueprintLibrary.description"),
    replaceButton: i18n("modules.applyMyBlueprintLibrary.replaceButton"),
    cancelButton: i18n("modules.applyMyBlueprintLibrary.cancelButton"),
  };

  const handleBackButtonClick = () => {
    dispatch(updateMyBlueprintLibraryModalContentShown("find"));
    dispatch(updateSelectedMyBlueprintLibraryBlueprint(undefined));
  };
  const closeModal = () => {
    dispatch(updateShowMyBlueprintLibraryModal(false));
    dispatch(updateMyBlueprintLibraryModalContentShown("find"));
    dispatch(updateSelectedMyBlueprintLibraryBlueprint(undefined));
  };

  const handleApplyBlueprint = () => {
    applyBlueprint(proposedBlueprintStructure);
    dispatch(updateShowMyBlueprintLibraryModal(false));
    dispatch(updateMyBlueprintLibraryModalContentShown("find"));
    dispatch(updateSelectedMyBlueprintLibraryBlueprint(undefined));
  };

  useEffect(() => {
    if (machineStructureData) {
      setCurrentStructure({
        id: "",
        name: machineStructureData.nodeset.name,
        created_at: machineStructureData.nodeset.created_at,
        author: machineStructureData.name,
        organization: "",
        description: "",
        verified: false,
        updated_at: null,
      });
    }

    if (selectedFindBlueprint) {
      findSelectedBlueprintUseCase(selectedFindBlueprint.id).then(
        (selectedBlueprintResponse) => {
          setProposedBlueprintStructure(selectedBlueprintResponse);
          const tileCollection = getTileCollection();
          if (selectedBlueprintResponse.dashboard?.data?.tiles?.length) {
            const tileParser = new TileStructureParser(
              selectedBlueprintResponse.dashboard.data,
              false,
            );
            tileCollection.setTiles(tileParser.getTiles());
          } else {
            tileCollection.setTiles([]);
          }
        },
      );
    }
  }, [machineStructureData]);

  return (
    <div className={styles[cssClassName]}>
      <GlassyModalHeader
        title={texts.subTitle}
        leftIcon={backArrowBlueIcon}
        leftIconFunction={handleBackButtonClick}
        rightIcon={closeIcon}
        rightIconFunction={closeModal}
      />
      <HorizontalSeparator verticalMargin={16} />
      <div>
        <p className={styles[`${cssClassName}__title`]}>{texts.title}</p>
        <p className={styles[`${cssClassName}__description`]}>
          {texts.description}
        </p>
        <Spacer16 />
        {currentStructure && selectedFindBlueprint && (
          <ReplaceBlueprintGradientBox
            currentBlueprint={currentStructure}
            selectedBlueprint={selectedFindBlueprint}
          />
        )}
      </div>
      <HorizontalSeparator verticalMargin={16} />
      <FlexRowContainer>
        <Button
          fun={handleApplyBlueprint}
          text={texts.replaceButton}
          disabled={!proposedBlueprintStructure}
        />
        <Button
          fun={closeModal}
          variant="secondary"
          text={texts.cancelButton}
        />
      </FlexRowContainer>
    </div>
  );
};
